import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import { Fab, makeStyles } from '@material-ui/core';
import { Menu, MenuItem } from '@mui/material';

import CheckIcon from '@material-ui/icons/Check';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

import Tooltip from 'components/Tooltip';
import XGrid3 from 'components/RMSXGrid';
import QuickFilterSearch from 'components/RMSSearch/components/QuickFilterSearch';

import { removePlaceAddress, getPtsPlace } from 'reducers/SearchReducer';
import { handleError } from 'reducers/ErrorReducer';
import { notify } from 'reducers/NotifierReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import { displayDateTime } from 'reducers/TimeReducer';
import { showAddressDialog, showPrimaryAddress } from 'reducers/PersonDialogsReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';

const useStyles = makeStyles((theme) => ({
  wrap: {
    padding: theme.spacing(3),
  },
  filterWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 8,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const bool = (params) => (params.value ? <CheckIcon /> : <span />);
const defaultColumns = [
  {
    field: 'IsPrimary',
    headerName: 'Is Primary',
    width: 120,
    format: 'bool',
    renderCell: bool,
    hide: false,
  },
  { field: 'AddressID', headerName: 'Address ID', width: 150, hide: false },
  { field: 'FullAddressText', headerName: 'Full Address', width: 250, hide: false },
  { field: 'AddressNumber', headerName: 'Address Number', width: 150, hide: false },
  { field: 'StreetName', headerName: 'Street Name', width: 150, hide: false },
  { field: 'StreetType', headerName: 'Street Type', width: 150, hide: false },
  { field: 'City', headerName: 'City', width: 150, hide: false },
  { field: 'State', headerName: 'State', width: 150, hide: false },
  { field: 'PostalCode', headerName: 'Postal Code', width: 150, hide: false },
  { field: 'PostalCodeExtension', headerName: 'Extension', width: 150, hide: false },
  { field: 'Country', headerName: 'Country', width: 150, hide: false },
  { field: 'AddressCategory', headerName: 'Address Category', width: 180, hide: false },
  {
    field: 'Created',
    headerName: 'Created',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
    hide: false,
  },
  { field: 'CreatedBy', headerName: 'Created By', width: 150, hide: false },
  {
    field: 'Updated',
    headerName: 'Updated',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
    hide: false,
  },
  { field: 'UpdatedBy', headerName: 'Updated By', width: 150, hide: false },
];
const PlaceAddress = (props) => {
  const classes = useStyles();
  const { ptsPlaceID, dataUpdate } = props;
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selection, setSelection] = useState(null);
  const [columns, setColumns] = useState(defaultColumns);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (ptsPlaceID) getData();
    return () => {
      mountedRef.current = false;
    };
  }, [ptsPlaceID]);

  useEffect(() => {
    if (dataUpdate?.type === 'Address') getData();
    if (dataUpdate && dataUpdate?.type === 'Delete Address') onDeleteClick();
  }, [dataUpdate]);

  const processData = (data) => data.map((row) => ({ ...row, id: row.ptsAddressID }));

  const getData = async () => {
    try {
      setLoaded(false);
      const response = await getPtsPlace(ptsPlaceID);
      if (!mountedRef.current) return;
      const placeAddressData = response?.PlaceAddress || [];
      const formatted = placeAddressData.map((pa) => ({
        ...pa.Address,
        ...pa,
        City: pa.Address?.City?.CityDescription || '',
      }));
      const processed = processData(formatted);
      setRows(processed);
      setFilteredRows(processed);
    } catch (err) {
      props.handleError(err, 'Error loading place addresses');
    } finally {
      if (mountedRef.current) setLoaded(true);
    }
  };

  const handleEditBtnClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const onAddClick = () => {
    props.showPrimaryAddress({
      ptsPlaceID,
      entityType: 'place',
    });
  };

  const onEditClick = () => {
    const item = rows.find((row) => row.ptsAddressID === selection);
    handleClose();
    if (!item) return;
    props.showPrimaryAddress({
      ...item,
      ptsPlaceID,
      entityType: 'place',
    });
  };

  const viewAddress = () => {
    const item = rows.find((row) => row.ptsAddressID === selection);
    if (!item) return;
    props.showAddressDialog({ ptsAddressID: item.ptsAddressID });
    handleClose();
  };

  const onDeleteClick = async () => {
    try {
      props.showSpinner();
      await removePlaceAddress({ ptsPlaceID, ptsAddressID: selection });
      props.notify('Address removed successfully', 'success');
      props.notifyDataUpdate({ type: 'Address' });
      setSelection(null);
    } catch (err) {
      props.handleError(err, 'Error removing address');
    } finally {
      props.hideSpinner();
    }
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.filterWrap}>
        <QuickFilterSearch rows={rows} columns={columns} setFilteredRows={setFilteredRows} />

        <div className={classes.actions}>
          <Tooltip title="Add Address">
            <Fab size="small" color="secondary" disabled={!props.canSave} onClick={onAddClick}>
              <AddIcon />
            </Fab>
          </Tooltip>
          {selection && (
            <>
              <Tooltip title="Edit Address" className="ml-2">
                <Fab
                  size="small"
                  color="secondary"
                  disabled={!props.canSave}
                  onClick={handleEditBtnClick}>
                  <EditIcon />
                </Fab>
              </Tooltip>
              <Tooltip title="Delete Address" className="ml-2">
                <Fab
                  size="small"
                  color="secondary"
                  disabled={!props.canSave}
                  onClick={onDeleteClick}>
                  <DeleteIcon />
                </Fab>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <MenuItem onClick={onEditClick}>Manage Primary</MenuItem>
        <MenuItem onClick={viewAddress}>Open Address Record</MenuItem>
      </Menu>

      <XGrid3
        name="PlaceAddress"
        columns={columns}
        setColumns={setColumns}
        rows={filteredRows}
        loading={!loaded}
        setSelection={setSelection}
      />
    </div>
  );
};
const mapStateToProps = (state) => ({
  dataUpdate: state.dataUpdate,
});

export default connect(mapStateToProps, {
  handleError,
  showSpinner,
  hideSpinner,
  showPrimaryAddress,
  showAddressDialog,
  notify,
  notifyDataUpdate,
})(PlaceAddress);
